import {useState, useEffect} from 'react';
import {AppText, AppBodyText} from '../../../Presentation/components/ui/Typography';
import {useServiceProviderStore} from '../store/serviceProviderStore';
import {Brand} from '../models/serviceProvider.model';

interface AssignedBrandsCellProps {
  providerId: string;
}

export default function AssignedBrandsCell ({providerId}: AssignedBrandsCellProps) {
  const {fetchProviderById} = useServiceProviderStore();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Simple cache to avoid repeated API calls
  const [cache, setCache] = useState<Record<string, Brand[]>>({});

  useEffect(() => {
    const fetchBrands = async () => {
      // Check cache first
      if (cache[providerId]) {
        setBrands(cache[providerId]);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Fetch the specific provider with their brands
        const providerWithBrands = await fetchProviderById(providerId);
        const brandsData = providerWithBrands.brands || [];

        // Update cache and state
        setCache(prev => ({...prev, [providerId]: brandsData}));
        setBrands(brandsData);
      } catch (err) {
        setError('فشل في جلب البراندات');
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [providerId, fetchProviderById, cache]);

  if (loading) {
    return <AppText className="text-gray-400">جاري التحميل...</AppText>;
  }

  if (error) {
    return <AppBodyText className="text-red-500">{error}</AppBodyText>;
  }

  if (brands.length === 0) {
    return <AppBodyText className="text-gray-500">لا توجد براندات مرتبطة</AppBodyText>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {brands.map((brand) => (
        <span
          key={brand.id}
          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-100 text-brand-800"
        >
          {brand.name}
        </span>
      ))}
    </div>
  );
}
